import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdminLog {
  id: string;
  admin_user_id: string;
  action_type: string;
  target_user_id: string | null;
  details: any;
  created_at: string;
  admin_email?: string;
}

export interface UserActivityLog {
  id: string;
  user_id: string;
  activity_type: string;
  description: string | null;
  metadata: any;
  created_at: string;
  user_email?: string;
}

export const useAdminLogs = () => {
  const [adminLogs, setAdminLogs] = useState<AdminLog[]>([]);
  const [userActivityLogs, setUserActivityLogs] = useState<UserActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { toast } = useToast();

  const fetchAdminLogs = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('admin_actions')
        .select('*, admin:admin_user_id(email)')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filterType !== 'all') {
        query = query.eq('action_type', filterType);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAdminLogs(data || []);
    } catch (error) {
      console.error('Error fetching admin logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch admin logs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivityLogs = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('user_activity_logs')
        .select('*, user:user_id(email)')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filterType !== 'all') {
        query = query.eq('activity_type', filterType);
      }

      const { data, error } = await query;

      if (error) throw error;
      setUserActivityLogs(data || []);
    } catch (error) {
      console.error('Error fetching user activity logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch user activity logs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = (type: 'admin' | 'user', format: 'csv' | 'json') => {
    try {
      const data = type === 'admin' ? adminLogs : userActivityLogs;
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-logs-${new Date().toISOString()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // CSV format
        const headers = Object.keys(data[0] || {});
        const csv = [
          headers.join(','),
          ...data.map(row => 
            headers.map(header => {
              const value = (row as any)[header];
              return typeof value === 'object' ? JSON.stringify(value) : value;
            }).join(',')
          )
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-logs-${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast({
        title: 'Success',
        description: 'Logs exported successfully'
      });
    } catch (error) {
      console.error('Error exporting logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to export logs',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchAdminLogs();
    fetchUserActivityLogs();

    // Real-time subscriptions
    const adminChannel = supabase
      .channel('admin-logs-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_actions'
        },
        () => {
          fetchAdminLogs();
        }
      )
      .subscribe();

    const userChannel = supabase
      .channel('user-activity-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_activity_logs'
        },
        () => {
          fetchUserActivityLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(adminChannel);
      supabase.removeChannel(userChannel);
    };
  }, [filterType]);

  return {
    adminLogs,
    userActivityLogs,
    loading,
    filterType,
    setFilterType,
    searchQuery,
    setSearchQuery,
    exportLogs,
    refresh: () => {
      fetchAdminLogs();
      fetchUserActivityLogs();
    }
  };
};
